/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Combobox } from "@headlessui/react";
import { useMemo, useState } from "react";

import { faTimes } from "@/utils/iconLibraryTreeShake";

const MAX_RESULTS = 25;

const DriverMultiSelect = ({
  drivers,
  selectedDrivers,
  setSelectedDrivers,
}: {
  drivers: DriverList[];
  selectedDrivers: string[];
  setSelectedDrivers: (drivers: string[]) => void;
}) => {
  const [query, setQuery] = useState("");

  const selectedDriverObjects = useMemo(
    () =>
      drivers
        .filter(driver => selectedDrivers.includes(driver.id))
        .sort(
          (a, b) =>
            a.lastName?.localeCompare(b.lastName) ||
            a.firstName?.localeCompare(b.firstName)
        ),
    [drivers, selectedDrivers]
  );

  const filteredDrivers = useMemo(() => {
    if (query.length === 0) return [];
    const q = query.toLowerCase();
    return drivers
      .filter(
        driver =>
          !selectedDrivers.includes(driver.id) &&
          (driver.displayName?.toLowerCase()?.includes(q) ||
            driver.firstName?.toLowerCase()?.includes(q) ||
            driver.lastName?.toLowerCase()?.includes(q))
      )
      .sort(
        (a, b) =>
          a.lastName?.localeCompare(b.lastName) ||
          a.firstName?.localeCompare(b.firstName)
      )
      .slice(0, MAX_RESULTS);
  }, [drivers, selectedDrivers, query]);

  return (
    <Combobox
      value={selectedDrivers}
      onChange={e => {
        setSelectedDrivers(e);
        setQuery("");
      }}
      multiple
    >
      <div className="relative">
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-500 bg-black px-2 py-1.5 focus-within:border-white">
          {selectedDriverObjects.map(driver => (
            <span
              key={driver.id}
              className="inline-flex items-center gap-1 rounded bg-slate-700 px-2 py-0.5 text-sm text-white"
            >
              {driver.firstName} {driver.lastName}
              <button
                title="Remove"
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setSelectedDrivers(selectedDrivers.filter(v => v !== driver.id));
                }}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className="h-3 w-3 text-slate-400 hover:text-white"
                />
              </button>
            </span>
          ))}
          <Combobox.Input
            className="min-w-[120px] flex-1 border-none bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-0"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={
              selectedDrivers.length > 0
                ? "Add another driver..."
                : "Search for a driver..."
            }
          />
        </div>
        <Combobox.Options className="absolute z-50 mt-1 w-full max-h-60 flex-col overflow-auto rounded-md bg-black py-1 text-sm shadow-lg ring-1 ring-white/10 focus:outline-none">
          {query.length === 0 ? (
            <div className="px-3 py-2 text-slate-500">Type to search drivers...</div>
          ) : filteredDrivers.length === 0 ? (
            <div className="px-3 py-2 text-slate-500">No drivers found.</div>
          ) : (
            filteredDrivers.map(driver => (
              <Combobox.Option
                key={driver.id}
                value={driver.id}
                className={({ active }) =>
                  `cursor-pointer select-none px-3 py-1.5 ${
                    active ? "bg-slate-700 text-white" : "text-slate-300"
                  }`
                }
              >
                {driver.firstName} {driver.lastName}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
};

export default DriverMultiSelect;
